import type { InfiniteData, QueryClient } from '@tanstack/react-query';
import type { Channel, Meeting, Message, Paginated, Task } from '@nexo/types';
import { keys } from './queries';

type MessagePages = InfiniteData<Paginated<Message>, string | null>;

/** Pure cache updaters shared by mutations (optimistic updates) and realtime handlers. */
export const cache = {
  /** Inserts or replaces a message. Matches optimistic messages by clientId. */
  upsertMessage(client: QueryClient, message: Message) {
    const matches = (item: Message) =>
      item.id === message.id ||
      (message.clientId !== undefined && item.clientId === message.clientId);
    // A thread panel may hold its own copy of the message.
    client.setQueryData<Message>(keys.message(message.id), (current) =>
      current ? message : current,
    );
    // Thread replies live in their thread, not in the channel timeline.
    if (message.parentId) {
      client.setQueryData<Message[]>(keys.thread(message.parentId), (list) => {
        if (!list) return list;
        return list.some(matches)
          ? list.map((item) => (matches(item) ? message : item))
          : [...list, message];
      });
      return;
    }
    // Keep the pinned list in sync when a message is pinned, unpinned or edited.
    client.setQueryData<Message[]>(keys.pins(message.channelId), (list) => {
      if (!list) return list;
      const rest = list.filter((m) => m.id !== message.id);
      return message.pinnedAt ? [message, ...rest] : rest;
    });
    client.setQueryData<Message[]>(keys.saved(message.teamId), (list) =>
      list?.map((m) => (m.id === message.id ? message : m)),
    );
    client.setQueryData<MessagePages>(keys.messages(message.channelId), (data) => {
      if (!data) return data;
      const exists = data.pages.some((page) => page.items.some(matches));
      const pages = data.pages.map((page, index) => {
        if (exists)
          return { ...page, items: page.items.map((item) => (matches(item) ? message : item)) };
        // Page 0 holds the newest messages, in chronological order.
        return index === 0 ? { ...page, items: [...page.items, message] } : page;
      });
      return { ...data, pages };
    });
  },

  removeMessage(client: QueryClient, channelId: string, predicate: (m: Message) => boolean) {
    for (const [key] of client.getQueriesData<Message[]>({ queryKey: ['thread'] })) {
      client.setQueryData<Message[]>(key, (list) => list?.filter((m) => !predicate(m)));
    }
    client.setQueryData<Message[]>(keys.pins(channelId), (list) =>
      list?.filter((m) => !predicate(m)),
    );
    client.setQueryData<MessagePages>(
      keys.messages(channelId),
      (data) =>
        data && {
          ...data,
          pages: data.pages.map((page) => ({
            ...page,
            items: page.items.filter((m) => !predicate(m)),
          })),
        },
    );
  },

  upsertTask(client: QueryClient, task: Task) {
    client.setQueryData<Task[]>(keys.tasks(task.teamId), (list) => {
      if (!list) return list;
      const exists = list.some((t) => t.id === task.id);
      return exists ? list.map((t) => (t.id === task.id ? task : t)) : [task, ...list];
    });
  },

  removeTask(client: QueryClient, teamId: string, taskId: string) {
    client.setQueryData<Task[]>(keys.tasks(teamId), (list) => list?.filter((t) => t.id !== taskId));
  },

  /** Realtime copies don't carry the organiser-only guest URL: keep the one we had. */
  upsertMeeting(client: QueryClient, meeting: Meeting) {
    const merge = (current: Meeting | undefined) =>
      current && meeting.guestUrl === null && current.guestUrl
        ? { ...meeting, guestUrl: meeting.cancelledAt ? null : current.guestUrl }
        : meeting;
    client.setQueryData<Meeting>(keys.meeting(meeting.id), (current) =>
      current ? merge(current) : current,
    );
    client.setQueryData<Meeting[]>(keys.meetings(meeting.teamId), (list) => {
      if (!list) return list;
      const exists = list.some((m) => m.id === meeting.id);
      const next = exists
        ? list.map((m) => (m.id === meeting.id ? merge(m) : m))
        : [...list, meeting];
      return next.sort((a, b) => a.startsAt.localeCompare(b.startsAt));
    });
  },

  upsertChannel(client: QueryClient, channel: Channel) {
    client.setQueryData<Channel[]>(keys.channels(channel.teamId), (list) => {
      if (!list) return list;
      return list.some((c) => c.id === channel.id)
        ? list.map((c) =>
            c.id === channel.id ? { ...c, ...channel, unreadCount: c.unreadCount } : c,
          )
        : [...list, channel];
    });
  },

  patchChannel(
    client: QueryClient,
    teamId: string,
    channelId: string,
    patch: (c: Channel) => Partial<Channel>,
  ) {
    client.setQueryData<Channel[]>(keys.channels(teamId), (list) =>
      list?.map((c) => (c.id === channelId ? { ...c, ...patch(c) } : c)),
    );
  },
};
